import React from "react";
import type { ReactNode } from "react";
import { BulkExportFormat, DimensionType, IDimension } from "../../../../types";
import { BulkCalendarControl, BulkCalendarControlDateRange } from "./controls/BulkCalendarControl";
import { BulkBaseIndexControl, BulkBaseIndexControlDateRange } from "./controls/BulkBaseIndexControl";
import { FormatControl } from "./controls/FormatControl";
import { TransferList } from "./controls/TransferList";
import { VirtualFrequencyControl, VirtualFrequencyValue } from "./controls/VirtualFrequencyControl";
import type { DimensionItemDTO, ICalendarItemDTO } from "../../../../api";
import type { IBulkExportStep } from "../../BulkExportStepper.types";
import { virtualFrequency } from "../../BulkExportStepper.constants";

export function createControl(
  dataSourceId: number,
  type: DimensionType,
  items: DimensionItemDTO<string | number>[] | undefined,
  allSteps: IBulkExportStep[],
  selection: (string | number)[],
  onChange: (selection: (string | number)[]) => void,
  format: BulkExportFormat,
  onChangeFormate: (formate: BulkExportFormat) => void,
  contextDimensions: IDimension[],
  isBulkSubscribtion: boolean
): ReactNode {
  const commonProps = {
    dataSourceId,
    onChange,
    items: items ?? [],
    selection,
  };
  const calendarProps = {
    ...commonProps,
    isBulkSubscribtion,
    items: commonProps.items as ICalendarItemDTO<string | number>[],
  };
  switch (type) {
    case DimensionType.CalendarAQM: {
      const availableDateRanges = [
        BulkCalendarControlDateRange.Annual,
        BulkCalendarControlDateRange.Quarter,
        BulkCalendarControlDateRange.Month,
      ];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.CalendarAQMW: {
      const availableDateRanges = [
        BulkCalendarControlDateRange.Annual,
        BulkCalendarControlDateRange.Quarter,
        BulkCalendarControlDateRange.Month,
        BulkCalendarControlDateRange.Week,
      ];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.CalendarAnnual: {
      const availableDateRanges = [BulkCalendarControlDateRange.Annual];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.CalendarWeek: {
      const availableDateRanges = [BulkCalendarControlDateRange.Week];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.CalendarDay: {
      const availableDateRanges = [BulkCalendarControlDateRange.Day];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }

    case DimensionType.BaseIndexAQM: {
      const availableDateRanges = [
        BulkBaseIndexControlDateRange.Annual,
        BulkBaseIndexControlDateRange.Quarter,
        BulkBaseIndexControlDateRange.Month,
      ];
      return <BulkBaseIndexControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.BaseIndexAQMW: {
      const availableDateRanges = [
        BulkBaseIndexControlDateRange.Annual,
        BulkBaseIndexControlDateRange.Quarter,
        BulkBaseIndexControlDateRange.Month,
        BulkBaseIndexControlDateRange.Week,
      ];
      return <BulkBaseIndexControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }
    case DimensionType.BaseIndexAnnual: {
      const availableDateRanges = [BulkBaseIndexControlDateRange.Annual];
      return <BulkBaseIndexControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }

    case DimensionType.Frequency: {
      const dateRangeTypes = [
        DimensionType.DynamicDateRangeAQM,
        DimensionType.DynamicDateRangeAQMWD,
        DimensionType.DynamicDateRangeAQMW,
        DimensionType.DynamicDateRangeAnnual,
        DimensionType.DynamicDateRangeWeek,
      ];
      const dateRangeStep = allSteps.find((s) => dateRangeTypes.includes(s.type))!;
      let availableFrequencies: VirtualFrequencyValue[] = [];
      switch (dateRangeStep.type) {
        case DimensionType.DynamicDateRangeAQM:
          availableFrequencies = [VirtualFrequencyValue.Annual, VirtualFrequencyValue.Quarter, VirtualFrequencyValue.Month];
          break;

        case DimensionType.DynamicDateRangeAQMWD:
          availableFrequencies = [
            VirtualFrequencyValue.Annual,
            VirtualFrequencyValue.Quarter,
            VirtualFrequencyValue.Month,
            VirtualFrequencyValue.Week,
            VirtualFrequencyValue.Day,
          ];
          break;
        case DimensionType.DynamicDateRangeAQMW:
          availableFrequencies = [
            VirtualFrequencyValue.Annual,
            VirtualFrequencyValue.Quarter,
            VirtualFrequencyValue.Month,
            VirtualFrequencyValue.Week,
          ];
          break;
        case DimensionType.DynamicDateRangeAnnual:
          availableFrequencies = [VirtualFrequencyValue.Annual];
          break;
        case DimensionType.DynamicDateRangeWeek:
          availableFrequencies = [VirtualFrequencyValue.Week];
          break;
        default:
          throw new Error("Unknown dynamic date range");
      }
      return (
        <VirtualFrequencyControl {...commonProps} availableFrequencies={availableFrequencies} isBulkSubscribtion={isBulkSubscribtion} />
      );
    }

    case DimensionType.DynamicDateRangeAQM:
    case DimensionType.DynamicDateRangeAQMW:
    case DimensionType.DynamicDateRangeAQMWD:
    case DimensionType.DynamicDateRangeAnnual:
    case DimensionType.DynamicDateRangeWeek: {
      const frequencyDimension = contextDimensions.find((d) => d.id === virtualFrequency.hierarchyIdentifier);
      const availableDateRanges = (frequencyDimension?.selection as number[] as BulkCalendarControlDateRange[]) || [
        VirtualFrequencyValue.Annual,
      ];
      return <BulkCalendarControl availableDateRanges={availableDateRanges} {...calendarProps} />;
    }

    case DimensionType.Configuration: {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <TransferList {...commonProps} />
          {!isBulkSubscribtion && <FormatControl value={format} onChange={onChangeFormate} />}
        </div>
      );
    }

    default:
      return <TransferList {...commonProps} />;
  }
}
